import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { Box, IconButton, Paper, Slide, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useOrientation } from '../../hooks/useOrientation';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  AMBIENT_GLOW,
  PRESS_TRANSITION,
  SONOS_BLUE,
  SURFACE_BORDER,
  sonosPanelHeaderSx,
  sonosPanelTitleSx,
} from './sonosUiStyles';

export const SONOS_SIDE_PANEL_WIDTH = 380;
export const SONOS_SIDE_PANEL_RIGHT = 11;
export const SONOS_SIDE_PANEL_TOP = 120;
export const SONOS_SIDE_PANEL_BOTTOM = 58;

export const SONOS_SIDE_PANEL_SHELL_SX = {
  color: 'white',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  borderRadius: '16px 16px 0 0',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.55)',
  border: `1px solid ${SURFACE_BORDER}`,
  borderTop: `2px solid ${SONOS_BLUE}`,
  backgroundColor: '#000',
  backgroundImage: AMBIENT_GLOW,
};

export interface SonosSideSlidePanelProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  headerLeft?: React.ReactNode;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
  zIndex?: number;
  /** Solo afecta el slide en vista horizontal. Vertical sigue entrando desde la izquierda. */
  horizontalSlideDirection?: 'left' | 'up';
}

export interface SonosSideSlidePanelHandle {
  close: () => void;
}

const SonosSideSlidePanel = forwardRef<SonosSideSlidePanelHandle, SonosSideSlidePanelProps>(({
  open,
  onClose,
  title,
  headerLeft,
  toolbar,
  footer,
  children,
  testId,
  zIndex = 1500,
  horizontalSlideDirection = 'left',
}, ref) => {
  const isHorizontal = useOrientation();
  const [isClosing, setIsClosing] = useState(false);
  /** Tras el primer open, el DOM del panel permanece montado (evita titileo remount). */
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    if (open) {
      setMounted(true);
      setIsClosing(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    window.setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 280);
  }, [onClose]);

  const requestClose = useOverlayBackButton(
    open && !isClosing,
    handleClose,
    `sonos-side-${testId ?? 'panel'}`,
  );

  useImperativeHandle(ref, () => ({ close: requestClose }), [requestClose]);

  if (!mounted) return null;

  const slideIn = open && !isClosing;

  const header = (
    <Box sx={sonosPanelHeaderSx}>
      <Box sx={{ flexShrink: 0, minWidth: 32, maxWidth: 80, display: 'flex', alignItems: 'center' }}>
        {headerLeft}
      </Box>
      <Typography component="div" sx={sonosPanelTitleSx}>
        {title}
      </Typography>
      <IconButton
        onClick={requestClose}
        sx={{
          color: 'white',
          p: 0.5,
          flexShrink: 0,
          transition: PRESS_TRANSITION,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          '&:active': { transform: 'scale(0.9)' },
        }}
      >
        <Close sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const panelBody = (
    <>
      {header}
      {toolbar}
      {children}
      {footer}
    </>
  );

  if (isHorizontal) {
    return (
      <Slide
        direction={horizontalSlideDirection}
        in={slideIn}
        timeout={280}
        appear
      >
        <Paper
          data-testid={testId}
          data-audio-no-swipe=""
          sx={{
            ...SONOS_SIDE_PANEL_SHELL_SX,
            position: 'fixed',
            right: SONOS_SIDE_PANEL_RIGHT,
            top: SONOS_SIDE_PANEL_TOP,
            bottom: SONOS_SIDE_PANEL_BOTTOM,
            width: SONOS_SIDE_PANEL_WIDTH,
            maxWidth: SONOS_SIDE_PANEL_WIDTH,
            m: 0,
            zIndex,
            // Fuera de animación: no interceptar toques bajo el panel cerrado.
            pointerEvents: slideIn ? 'auto' : 'none',
          }}
        >
          {panelBody}
        </Paper>
      </Slide>
    );
  }

  return (
    <Box
      data-testid={testId}
      data-audio-no-swipe=""
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        pointerEvents: slideIn ? 'auto' : 'none',
        visibility: slideIn || isClosing ? 'visible' : 'hidden',
      }}
    >
      <Box
        onClick={requestClose}
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          opacity: slideIn ? 1 : 0,
          transition: 'opacity 200ms ease',
          pointerEvents: slideIn ? 'auto' : 'none',
        }}
      />
      <Slide direction="left" in={slideIn} timeout={280} appear>
        <Paper
          data-audio-no-swipe=""
          sx={{
            ...SONOS_SIDE_PANEL_SHELL_SX,
            position: 'fixed',
            top: 40,
            left: '1%',
            right: '1%',
            bottom: 58,
            width: '98%',
            zIndex: zIndex + 1,
            pointerEvents: slideIn ? 'auto' : 'none',
          }}
        >
          {panelBody}
        </Paper>
      </Slide>
    </Box>
  );
});

SonosSideSlidePanel.displayName = 'SonosSideSlidePanel';

export default SonosSideSlidePanel;
